import { useContext } from 'react';
import { type Atom, useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';

import {
  aiBuilderChatStateAtom,
  AiBuilderChatStoreContext,
} from '../stores/aiBuilderChatStore';
import { type AiBuilderChatState } from '../stores/types';

export type AiBuilderChatPanelSelector = Pick<AiBuilderChatState, 'panelOpen'>;

export type AiBuilderChatMessagesSelector = Pick<
  AiBuilderChatState,
  'conversationEntryIntent' | 'conversationQuestionState' | 'messages'
>;

export type AiBuilderChatComposerSelector = Pick<
  AiBuilderChatState,
  | 'draft'
  | 'isGenerating'
  | 'messages'
  | 'panelOpen'
  | 'placeholder'
  | 'selectedFile'
  | 'selectedQuizContext'
  | 'turnInProgress'
  | 'usage'
>;

const createMissingProviderError = (hookName: string) =>
  `${hookName} must be used within AiBuilderChatProvider`;

const shallowEqualAiBuilderChatSlice = <Slice extends object>(
  previous: Slice,
  next: Slice,
): boolean => {
  if (Object.is(previous, next)) return true;

  const previousKeys = Object.keys(previous) as Array<keyof Slice>;
  const nextKeys = Object.keys(next) as Array<keyof Slice>;

  if (previousKeys.length !== nextKeys.length) return false;

  return previousKeys.every(
    key =>
      Object.prototype.hasOwnProperty.call(next, key) &&
      Object.is(previous[key], next[key]),
  );
};

export const aiBuilderChatPanelSelectorAtom = selectAtom(
  aiBuilderChatStateAtom,
  (state): AiBuilderChatPanelSelector => ({
    panelOpen: state.panelOpen,
  }),
  shallowEqualAiBuilderChatSlice,
);

export const aiBuilderChatMessagesSelectorAtom = selectAtom(
  aiBuilderChatStateAtom,
  (state): AiBuilderChatMessagesSelector => ({
    conversationEntryIntent: state.conversationEntryIntent,
    conversationQuestionState: state.conversationQuestionState,
    messages: state.messages,
  }),
  shallowEqualAiBuilderChatSlice,
);

export const aiBuilderChatIsGeneratingSelectorAtom = selectAtom(
  aiBuilderChatStateAtom,
  state => state.isGenerating,
);

export const aiBuilderChatComposerSelectorAtom = selectAtom(
  aiBuilderChatStateAtom,
  (state): AiBuilderChatComposerSelector => ({
    draft: state.draft,
    isGenerating: state.isGenerating,
    messages: state.messages,
    panelOpen: state.panelOpen,
    placeholder: state.placeholder,
    selectedFile: state.selectedFile,
    selectedQuizContext: state.selectedQuizContext,
    turnInProgress: state.turnInProgress,
    usage: state.usage,
  }),
  shallowEqualAiBuilderChatSlice,
);

export const useAiBuilderChatStore = (hookName = 'useAiBuilderChatStore') => {
  const store = useContext(AiBuilderChatStoreContext);

  if (!store) {
    throw new Error(createMissingProviderError(hookName));
  }

  return store;
};

export const aiBuilderChatSessionIdSelectorAtom = selectAtom(
  aiBuilderChatStateAtom,
  state => state.sessionId,
);

/**
 * Provider 밖에서도 안전하게 chat session id 를 읽는다 (없으면 null).
 * 저장 이벤트(create_complete/edit_complete)에 ai_builder_session_id attribution 을
 * 붙이기 위한 조회 전용 훅 — chat 상태를 구독하지만 UI 를 소유하지 않는다.
 */
export const useAiBuilderChatSessionIdSafe = (): string | null => {
  const store = useContext(AiBuilderChatStoreContext);
  const sessionId = useAtomValue(
    aiBuilderChatSessionIdSelectorAtom,
    store ? { store } : undefined,
  );

  return store ? sessionId : null;
};

export const useAiBuilderChatStoreAtomValue = <Value>(
  atom: Atom<Value>,
  hookName = 'useAiBuilderChatStoreAtomValue',
) => {
  const store = useAiBuilderChatStore(hookName);

  return useAtomValue(atom, { store });
};

export const useAiBuilderChatPanelSelector = () =>
  useAiBuilderChatStoreAtomValue(
    aiBuilderChatPanelSelectorAtom,
    'useAiBuilderChatPanelSelector',
  );

export const useAiBuilderChatMessagesSelector = () =>
  useAiBuilderChatStoreAtomValue(
    aiBuilderChatMessagesSelectorAtom,
    'useAiBuilderChatMessagesSelector',
  );

export const useAiBuilderChatComposerSelector = () =>
  useAiBuilderChatStoreAtomValue(
    aiBuilderChatComposerSelectorAtom,
    'useAiBuilderChatComposerSelector',
  );

export const useAiBuilderChatIsGeneratingSelector = () =>
  useAiBuilderChatStoreAtomValue(
    aiBuilderChatIsGeneratingSelectorAtom,
    'useAiBuilderChatIsGeneratingSelector',
  );
